import React from 'react';
import { useTranslation } from 'react-i18next';
import { AttendanceCountChip } from '@/pages/Overview/AttendanceCountChip';
import { AttendanceGauge } from '@/pages/Overview/AttendanceGauge';
import type { DisciplineTypeStatistics } from '@/pages/Overview/hooks/useAttendanceStatistics';
import { JustifiedReasons } from '@/pages/Overview/JustifiedReasons';
import { disciplineTypeLabelKeys } from '@/types/overview';
import { getPercent } from '@/utils/text';

interface Props {
  statistics: DisciplineTypeStatistics;
  hasTitle: boolean;
}

export const MandatoryContentCard: React.FC<Props> = ({
  statistics,
  hasTitle,
}) => {
  const { t } = useTranslation();
  const {
    type,
    attendableLessonsCount,
    total,
    justifiedCount,
    sicknessCount,
    otherCount,
    nonJustifiedCount,
  } = statistics;
  const attended = attendableLessonsCount - total;

  return (
    <div
      className={`shadow-card flex flex-col gap-4 rounded-xl bg-popover p-4 md:flex-row md:gap-6 md:p-6 ${hasTitle ? 'md:items-start' : 'md:items-center'}`}
    >
      <div className="flex w-full flex-col gap-4 md:w-auto md:shrink-0 md:gap-5">
        {hasTitle && (
          <p className="text-subhead-14 md:text-callout-16 text-foreground">
            {t(disciplineTypeLabelKeys[type])}
          </p>
        )}
        <div className="flex items-center gap-5 md:items-end md:gap-8">
          <AttendanceGauge
            percent={getPercent(attended, attendableLessonsCount)}
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <p className="text-body-13 text-subtle-foreground">
              {t('overview.attendance.totalAttendance')}
            </p>
            <p className="text-callout-16 text-primary">
              {t('overview.attendance.attendedCount', {
                attended,
                total: attendableLessonsCount,
              })}
            </p>
          </div>
        </div>
      </div>

      <div className="h-px w-full shrink-0 bg-border md:h-auto md:w-px md:self-stretch" />

      <div className="flex w-full flex-col gap-3 md:flex-1">
        <div className="flex items-center gap-1">
          <span className="text-body-14 text-muted-foreground md:text-subtle-foreground">
            {t('overview.attendance.absences')}
          </span>
          <span className="text-subhead-14 text-foreground">{total}</span>
        </div>
        <div className="flex flex-col gap-2">
          <div className="bg-info-surface flex items-center gap-1 rounded-[10px] px-3 py-2">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <p className="text-body-14 truncate text-foreground">
                {t('overview.attendance.justified')}
              </p>
              <JustifiedReasons
                sicknessCount={sicknessCount}
                otherCount={otherCount}
              />
            </div>
            <AttendanceCountChip isJustified count={justifiedCount} />
          </div>
          <div className="bg-on-primary-surface flex items-center gap-3 rounded-[10px] px-3 py-2">
            <p className="text-body-14 min-w-0 flex-1 truncate text-foreground">
              {t('overview.attendance.nonJustified')}
            </p>
            <AttendanceCountChip
              isJustified={false}
              count={nonJustifiedCount}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
